/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ButtonStyled, TextStyled, Typeahead } from '@nike/nike-design-system-components';
import { func, bool, string, object, array } from 'prop-types';
import { Box } from 'rebass';
import { showToast } from '../../../components/toast/actions';

import Modal from '../../../modal/Modal';

import { getCatalogTypesData } from '../../catalogTypes/selectors';

import { bulkSaveAdminDepartmentCatalogTypes as saveCatalogTypes } from '../actions';
import { TranslatedCopy } from '../../../intl/TranslatedCopy';

const title = 'Add Catalog Type';

const customSearchFn = (itemsToSearch, inputValue) => {
  return itemsToSearch.filter(item => item?.toLowerCase()?.indexOf(inputValue?.toLowerCase()) > -1);
};

const AddCatalogTypesModal = ({
  salesOrg,
  catalogTypes,
  closeModal,
  isOpen,
  bulkUpdateDepartmentCatalogTypes,
  saveCatalogTypes,
  showToast,
  allDepartments,
  selectDepartmentIds
}) => {
  const [selectedCatalogTypeId, setSelectedCatalogTypeId] = useState('');
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [expandDepartments, setExpandDepartments] = useState(false);

  const handleCatalogTypeChange = catalogTypeName => {
    // temporary workaround until Typeahead passes the entire catalogType object that was selected
    const catalogTypeValues = Object.values(catalogTypes);
    const catalogTypeId = catalogTypeValues.find(catalogType => catalogType.name === catalogTypeName)?.id;

    setSelectedCatalogTypeId(catalogTypeId);
  };

  const handleAddCatalogTypeToDepartment = async () => {
    const departments = allDepartments.filter(d => selectDepartmentIds.includes(d.id));
    const catalogTypeToAdd = catalogTypes[selectedCatalogTypeId];
    const departmentIdToCatalogTypes = departments.reduce(
      (departmentToCatalogTypes, department) => ({
        ...departmentToCatalogTypes,
        [department.id]: department.catalogTypes.find(ct => ct.id === catalogTypeToAdd.id)
          ? department.catalogTypes
          : [...department.catalogTypes, catalogTypeToAdd]
      }),
      {}
    );

    try {
      await saveCatalogTypes({ departmentIdToCatalogTypes });
      bulkUpdateDepartmentCatalogTypes(departmentIdToCatalogTypes);
    } catch (error) {
      showToast({
        type: 'error',
        message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
        error
      });
    }
  };

  useEffect(() => {
    const selectedDepartments = allDepartments.filter(d => selectDepartmentIds.includes(d.id));
    const ctDropdownOptions = Object.values(catalogTypes)
      .filter(
        catalogType =>
          !!catalogType &&
          !selectedDepartments.every(dept =>
            dept.catalogTypes.find(deptCatalogType => deptCatalogType.id === catalogType.id)
          ) &&
          catalogType.salesOrg === salesOrg
      )
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(catalogType => catalogType.name);

    setDropdownOptions(ctDropdownOptions);
  }, [JSON.stringify(allDepartments), selectDepartmentIds]);

  const content = () => {
    const departmentNames = [
      ...new Set(selectDepartmentIds.map(id => allDepartments.find(d => d.id === id).name))
    ].sort();

    const hasOverflow = departmentNames.length > 12;

    return (
      <Box width='100%' minHeight='350px'>
        <div>
          <TextStyled appearance='body1Strong' css={{ paddingBottom: '5px' }}>
            Selected Departments:
          </TextStyled>
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              height: expandDepartments || !hasOverflow ? 'unset' : '120px',
              overflowY: expandDepartments ? 'visible' : 'hidden'
            }}
          >
            {departmentNames.map(dept => (
              <TextStyled appearance='body2'>{dept}</TextStyled>
            ))}
          </Box>
          {hasOverflow && (
            <ButtonStyled
              appearance='text'
              size='small'
              onClick={() => setExpandDepartments(!expandDepartments)}
              css={{
                float: 'right'
              }}
            >
              {expandDepartments ? '...Show Less' : '...Show More'}
            </ButtonStyled>
          )}
        </div>

        <Typeahead
          id='addCatalogTypeField'
          label='Catalog Type'
          noResultsMessage='No matches found.'
          searchableItems={dropdownOptions}
          onItemSelect={handleCatalogTypeChange}
          style={{ marginTop: '10px' }}
          inputProps={{ 'data-testid': 'addCatalogTypeField' }}
          searchFn={customSearchFn}
          onFocus={() => setExpandDepartments(false)}
        />
      </Box>
    );
  };

  const footer = ({ isLoading, toggleIsLoading }) => {
    const isSubmitButtonDisabled = isLoading || !selectedCatalogTypeId;

    const handleSubmit = async () => {
      toggleIsLoading(true, 'Adding Catalog Type...');

      await handleAddCatalogTypeToDepartment();

      toggleIsLoading(false);

      closeModal();
    };

    return (
      <ButtonStyled
        data-testid='addProducts-submit-button'
        type='button'
        appearance='primary'
        background='light'
        size='small'
        onClick={handleSubmit}
        disabled={isSubmitButtonDisabled}
      >
        Add
      </ButtonStyled>
    );
  };

  return (
    <Modal
      title={`${selectDepartmentIds.length > 1 ? 'Bulk ' : ''}${title}`}
      content={content}
      footer={footer}
      isOpen={isOpen}
      closeModal={closeModal}
      unwrapped
    />
  );
};

AddCatalogTypesModal.propTypes = {
  closeModal: func.isRequired,
  isOpen: bool,
  salesOrg: string.isRequired,
  catalogTypes: object,
  bulkUpdateDepartmentCatalogTypes: func.isRequired,
  saveCatalogTypes: func.isRequired,
  showToast: func.isRequired,
  allDepartments: array,
  selectDepartmentIds: array
};

AddCatalogTypesModal.defaultProps = {
  catalogTypes: {},
  allDepartments: [],
  selectDepartmentIds: [],
  isOpen: false
};

const mapStateToProps = state => {
  return {
    catalogTypes: getCatalogTypesData(state)
  };
};

export default connect(mapStateToProps, { saveCatalogTypes, showToast })(AddCatalogTypesModal);
